import { BrowserClient, Hub, Severity } from '@sentry/browser';
import { WidgetMeta } from '../../types/WidgetProps';
import { getPageFingerprint } from './getPageFingerprint';

const SENTRY_HUB = initSentry();

function initSentry(): Hub {
  const client = new BrowserClient({
    dsn: 'https://f7c9f70c1b7649f6b53f72e962d018d8@sentry.wixpress.com/15',
    environment: process.env.NODE_ENV,
    maxBreadcrumbs: 50,
    beforeSend(event, hint) {
      if (!(process.env.NODE_ENV === 'production')) {
        return null;
      }
      const error = hint.originalException as Error;
      const ignoreEvent = ignoreMessageRegex.some((rg) => error.message.match(rg));
      return ignoreEvent ? null : event;
    },
  });
  return new Hub(client);
}

export interface SentryData {
  locale: string;
  href: string;
  meta: WidgetMeta;
  release: string;
}

const enrichSentryWithData = (params: SentryData) => {
  const page = getPageFingerprint();
  SENTRY_HUB.configureScope((scope) => {
    scope.setTag('locale', params.locale);
    scope.setTag('href', params.href);
    scope.setExtra('meta', JSON.stringify(params.meta));
    scope.setTag('msid', params.meta.siteId);
    scope.setTag('release', params.release);
    scope.setTag('page', page);
  });
};

const setSentryTag = (tag: { key: string; value: string }) => SENTRY_HUB.setTag(tag.key, tag.value);

function logXhrError({ axiosError, message, fingerprint, reqArgs }) {
  const error = new Error(message);
  SENTRY_HUB.withScope(function (scope) {
    // https://github.com/axios/axios#handling-errors
    if (axiosError.response) {
      const { data, status, headers } = axiosError.response;
      scope.setExtra(
        'payload',
        JSON.stringify({
          data,
          status,
          headers,
        }),
      );
      scope.setExtra('reqArgs', JSON.stringify(reqArgs));
    } else if (axiosError.request) {
      scope.setExtra('paylaod', 'No response, connection timeout');
    }

    if (fingerprint) {
      const page = getPageFingerprint();
      const fp = Array.isArray(fingerprint) ? fingerprint : [fingerprint];
      fp.push(axiosError?.response?.status);
      fp.push(page);
      scope.setFingerprint(fp);
    }
    SENTRY_HUB.captureException(error);
  });
}

function logGeneralError({
  message,
  payload,
  fingerprint,
  severity,
}: {
  message: string;
  payload?: any;
  fingerprint?: string[];
  severity?: Severity;
}): string | void {
  if (process.env.NODE_ENV === 'test') {
    return;
  }

  const error = new Error(message);
  const page = getPageFingerprint();

  if (!(process.env.NODE_ENV === 'production')) {
    return console.error(error, message, payload);
  }

  let eventId: string;
  SENTRY_HUB.withScope(function (scope) {
    scope.setExtra('payload', JSON.stringify(payload));

    if (fingerprint) {
      const fp = Array.isArray(fingerprint) ? fingerprint : [fingerprint];
      fp.push(page);
      scope.setFingerprint(fp);
    }
    if (severity) {
      scope.setLevel(severity);
    }

    eventId = SENTRY_HUB.captureException(error);
  });

  return eventId;
}

// Error messages, relevant mostly for PayPal
// window.
const ignoreMessageRegex = [
  /window closed/i,
  /Request to post.*logger\/api\/egglor/i,
  /Window navigated away/i,
  /Access is denied/i,
  /Target window is closed/i,
  /No ack for postMessage trigger/i,
  /Window cleaned up before response/i,
  /Window closed for postrobot_method before ack/i,
];

export { enrichSentryWithData, setSentryTag, logGeneralError, logXhrError };
